import {
	AfterViewInit,
	Directive,
	ElementRef,
	inject,
	input,
	OnDestroy,
	PLATFORM_ID,
} from '@angular/core';
import { abbreviateProduct, DataLayerService, verifyCleso, verifyUgso } from '../..';
import { isPlatformBrowser } from '@angular/common';
import { Product } from '@uc/shared/data-models';

type EventType = 'click' | 'view';
type EventName = 'university_profile_view' | 'university_profile_click';

@Directive({
	selector: '[ucUniProfileGAEvent]',
	standalone: true,
	host: {
		'(click)': 'onClick()',
	},
})
export class UniProfileGAEventDirective implements AfterViewInit, OnDestroy {
	eventTypes = input.required<EventType[]>();
	uniId = input.required<number>();
	uniName = input.required<string>();
	ugSearchOrderPosition = input.required<number | null>();
	cleSearchOrderPosition = input.required<number | null>();
	product = input.required<Product | ''>();

	private _platformId = inject(PLATFORM_ID);
	private _observer: IntersectionObserver | null = null;
	private _impressionTracked = false;

	constructor(
		private _el: ElementRef,
		private _dataLayerSrv: DataLayerService,
	) {}

	onClick() {
		if (!this.eventTypes().includes('click')) return;
		this._dataLayerSrv.push(this._getTrackingData('click'));
	}

	ngAfterViewInit() {
		const shouldTrackImpression = this.eventTypes().includes('view');
		const isBrowser = isPlatformBrowser(this._platformId);

		if (shouldTrackImpression && isBrowser) {
			this._observer = new IntersectionObserver((entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting && !this._impressionTracked) {
						this._impressionTracked = true;
						this._dataLayerSrv.push(this._getTrackingData('view'));
						this._observer?.disconnect();
					}
				}
			});

			this._observer.observe(this._el.nativeElement);
		}
	}

	ngOnDestroy() {
		this._observer?.disconnect();
	}

	private _getEventName(type: EventType): EventName {
		return type === 'view' ? 'university_profile_view' : 'university_profile_click';
	}

	private _getEvent(type: EventType) {
		return type === 'view' ? 'universityProfileView' : 'uniProfileClick';
	}

	private _getTrackingData(type: EventType) {
		const product = this.product();
		const ugso = verifyUgso(this.ugSearchOrderPosition(), product);
		const cleso = verifyCleso(this.cleSearchOrderPosition(), product);

		const trackingData = Object.assign(
			{
				eventName: this._getEventName(type),
				university: this.uniId(),
				universityName: this.uniName(),
				product: abbreviateProduct(product),
				event: this._getEvent(type),
			},
			ugso ? { ugso, cleso: 'n/a' } : {},
			cleso ? { cleso, ugso: 'n/a' } : {},
		);

		return trackingData;
	}
}
